import React from 'react';
import cn from 'classnames';
import { S2Typography } from 'apps/sigma2-ui/src/components/ui/Typography';
import { TouchTarget } from 'apps/sigma2-ui/src/components/ui/TouchTarget';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { DropdownChevronDownSvg } from 'apps/sigma2-ui/src/components/svg/DropdownChevronDown';
import { IconInformationSvg } from 'apps/sigma2-ui/src/components/svg/IconInformation';
import { LightTooltip } from 'apps/sigma2-ui/src/components/ui/Tooltip';

export interface EventStudyButtonProps {
  onClick: () => void;

  /**
   * Whether the button is disabled
   */
  disabled: boolean;
}

export const EventStudyButton = ({
  onClick,
  disabled,
}: EventStudyButtonProps) => {
  const language = useLanguage();

  return (
    <div className={'inline-block max-w-full'}>
      <TouchTarget
        disabled={disabled}
        content={({ hovered }) => {
          return (
            <span
              className={cn(
                'flex rounded py-2 px-3 border-[1px] items-center transition-all',
                {
                  'opacity-50 cursor-not-allowed': disabled,
                  'opacity-100 cursor-pointer': !disabled,
                  'bg-white border-gray-200': !hovered,
                  'bg-royal-100 border-royal-400': hovered,
                },
              )}
            >
              <div className="flex-1">
                <S2Typography
                  translatedContent={'Run Event Study'}
                  variant={'headline-7-alt'}
                  textColorVariant={hovered ? 'royal-700' : 'gray-900'}
                />
              </div>

              <DropdownChevronDownSvg
                svg={{
                  className: cn('w-[1.5em] h-[1.5em] transition-all', {
                    'text-royal-700': hovered,
                    'text-gray-900': !hovered,
                  }),
                }}
              />

              <LightTooltip
                title={
                  <S2Typography
                    as={'span'}
                    variant={'body-7'}
                    textColorVariant="gray-1000"
                    translatedContent={language.eventStudyTooltip()}
                  />
                }
                arrow
              >
                <span className="pl-1">
                  <IconInformationSvg
                    svg={{
                      className: cn('relative w-5 h-5', {
                        'text-royal-700': hovered,
                        'text-gray-400': !hovered,
                      }),
                    }}
                  />
                </span>
              </LightTooltip>
            </span>
          );
        }}
        behavior={{ type: 'onClick', onClick }}
      />
    </div>
  );
};
